import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Row, Col } from 'react-bootstrap';
import { cautionService, checkService } from '../../services/api';
import { Caution, Check } from '../../types';
import { toast } from 'react-toastify';

interface CautionFormProps {
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
  caution?: Caution | null;
}

const CautionForm: React.FC<CautionFormProps> = ({ show, onHide, onSuccess, caution }) => {
  const [formData, setFormData] = useState({
    reference: '',
    market_name: '',
    amount: '',
    method: 'cash',
    check_id: '',
    check_number: '',
    bank_name: '',
    submission_date: '',
    expiry_date: '',
    notification_date: '',
    notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [availableChecks, setAvailableChecks] = useState<Check[]>([]);

  useEffect(() => {
    if (show) {
      fetchAvailableChecks();
    }
  }, [show]);

  useEffect(() => {
    if (caution) {
      setFormData({
        reference: caution.reference || '',
        market_name: caution.market_name || '',
        amount: caution.amount.toString() || '',
        method: caution.method || 'cash',
        check_id: caution.check_id?.toString() || '',
        check_number: caution.check?.check_number || '',
        bank_name: caution.check?.checkbook?.bank_name || '',
        submission_date: caution.submission_date || '',
        expiry_date: caution.expiry_date || '',
        notification_date: caution.notification_date || '',
        notes: caution.notes || '',
      });
    } else {
      setFormData({
        reference: '',
        market_name: '',
        amount: '',
        method: 'cash',
        check_id: '',
        check_number: '',
        bank_name: '',
        submission_date: '',
        expiry_date: '',
        notification_date: '',
        notes: '',
      });
    }
  }, [caution, show]);

  const fetchAvailableChecks = async () => {
    try {
      const response = await checkService.getAll({ status: 'unused' });
      console.log('Response checks:', response.data);
      // Essayer différentes structures de réponse
      const checks = response.data.data?.data || response.data.data || response.data || [];
      console.log('Available checks:', checks);
      setAvailableChecks(checks);
    } catch (error) {
      console.error('Error loading checks:', error);
      toast.error('Erreur lors du chargement des chèques');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = {
        ...formData,
        amount: parseFloat(formData.amount),
        check_id: formData.method === 'check' && formData.check_id ? parseInt(formData.check_id) : null
      };

      if (caution) {
        await cautionService.update(caution.id, data);
        toast.success('Caution modifiée avec succès');
      } else {
        await cautionService.create(data);
        toast.success('Caution créée avec succès');
      }
      onSuccess();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{caution ? 'Modifier' : 'Nouvelle'} caution</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Référence</Form.Label>
                <Form.Control
                  type="text"
                  value="Générée automatiquement"
                  readOnly
                  className="bg-light"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Nom du marché *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.market_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, market_name: e.target.value }))}
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Montant *</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Méthode *</Form.Label>
                <Form.Select
                  value={formData.method}
                  onChange={(e) => setFormData(prev => ({ ...prev, method: e.target.value, check_id: '' }))}
                  required
                >
                  <option value="cash">Espèces</option>
                  <option value="check">Chèque</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {formData.method === 'check' && (
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Chèque *</Form.Label>
                  <Form.Select
                    value={formData.check_id}
                    onChange={(e) => {
                      const selectedCheck = availableChecks.find(check => check.id.toString() === e.target.value);
                      setFormData(prev => ({ 
                        ...prev, 
                        check_id: e.target.value,
                        check_number: selectedCheck?.check_number || '',
                        bank_name: selectedCheck?.checkbook?.bank_name || ''
                      }));
                    }}
                    required
                    disabled={!!caution}
                  >
                    <option value="">Sélectionner un chèque</option>
                    {availableChecks.map((check) => (
                      <option key={check.id} value={check.id}>
                        {check.check_number} - {check.checkbook?.bank_name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>N° Chèque</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.check_number || ''}
                    readOnly
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Banque</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.bank_name || ''}
                    readOnly
                  />
                </Form.Group>
              </Col>
            </Row>
          )}

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Date de notification</Form.Label>
                <Form.Control
                  type="date"
                  value={formData.notification_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, notification_date: e.target.value }))}
                />
                <Form.Text className="text-muted">
                  Date pour rappel de récupération de la caution
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Date de soumission *</Form.Label>
                <Form.Control
                  type="date"
                  value={formData.submission_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, submission_date: e.target.value }))}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Date d'expiration *</Form.Label>
                <Form.Control
                  type="date"
                  value={formData.expiry_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, expiry_date: e.target.value }))}
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Annuler
          </Button>
          <Button variant="primary" type="submit" disabled={loading}>
            {loading ? 'Sauvegarde...' : 'Sauvegarder'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CautionForm;